"use client";

import type { ReactNode } from "react";
import { useTranslations } from "next-intl";
import { Button } from "@/app/components/ui/Button";
import { Input } from "@/app/components/ui/Input";
import type { PersonalTrainerServiceRequest } from "@/app/generated/api/schemas";

type Props<Service extends PersonalTrainerServiceRequest> = {
  services: Service[];
  onChange: (services: Service[]) => void;
  createService: () => Service;
  namespace: "apps.industrySetup.personalTrainer" | "apps.industrySetup.hairSalon";
  renderOptions?: (service: Service, update: (patch: Partial<Service>) => void) => ReactNode;
};

export function SetupServicesFields<Service extends PersonalTrainerServiceRequest>({
  services, onChange, createService, namespace, renderOptions,
}: Props<Service>) {
  const t = useTranslations(namespace);
  const update = (key: string, patch: Partial<Service>) => onChange(
    services.map((service) => service.key === key ? { ...service, ...patch } : service),
  );

  return <>
    {services.map((service, index) => <fieldset key={service.key} className="space-y-4 rounded-xl border border-border p-5">
      <legend className="px-2 text-sm font-semibold">{t("service", { number: index + 1 })}</legend>
      <Input label={t("serviceName")} required maxLength={255} value={service.name}
        onChange={(event) => update(service.key, { ...service, name: event.target.value })} />
      <div className="grid gap-4 sm:grid-cols-2">
        <Input label={t("duration")} type="number" min={5} max={480} required value={service.durationMinutes === 0 ? "" : service.durationMinutes}
          onChange={(event) => update(service.key, { ...service, durationMinutes: Number(event.target.value) })} />
        <Input label={t("price")} type="number" min={0} max={99999999.99} step="0.01" value={service.price ?? ""}
          onChange={(event) => update(service.key, { ...service, price: event.target.value || null })} />
      </div>
      {renderOptions?.(service, (patch) => update(service.key, patch))}
      <Button type="button" variant="ghost" disabled={services.length === 1}
        aria-label={t("removeService", { number: index + 1 })}
        onClick={() => onChange(services.filter((item) => item.key !== service.key))}>{t("remove")}</Button>
    </fieldset>)}
    <Button type="button" variant="secondary" disabled={services.length >= 50}
      onClick={() => onChange([...services, createService()])}>{t("addService")}</Button>
  </>;
}
